
import React, { useState, useEffect, useMemo } from "react";
import DashboardFooter from "../components/DashboardFooter";
import { NotificationManager } from "react-notifications";
import "../assets/css/appointmentCard.css";

// List = GET base. Detail = GET base + `${id}/`. Update = PATCH base + `${id}/`.
const API_URL = `${process.env.REACT_APP_HOST_API_URL}/admin-user/package-feedback/`;

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatDateForInput = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getThisMonthRange = () => {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: formatDateForInput(first), to: formatDateForInput(today) };
};

const getPreviousMonthRange = () => {
  const today = new Date();
  const firstPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastPrev = new Date(today.getFullYear(), today.getMonth(), 0); // day 0 = last day of previous month
  return { from: formatDateForInput(firstPrev), to: formatDateForInput(lastPrev) };
};

const initials = (name) => {
  if (!name) return "??";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
};

// ---------- small inline icons (no extra dependency) ----------
const Icon = {
  Star: (props) => (
    <svg viewBox="0 0 24 24" width="14" height="14" {...props}>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  ),
  Pencil: (props) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  ),
  X: (props) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  Chevron: (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
};


const EmployeeFeedback = () => {
  const token = localStorage.getItem("token");
  const authHeaders = token ? { Authorization: "Bearer " + token } : {};

  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState("");
  const [status, setStatus] = useState(false);

  const [search, setSearch] = useState("");

  // ---------- Date range state ----------
  const defaultRange = getThisMonthRange();
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);
  const [activeRange, setActiveRange] = useState("current"); // 'current' | 'previous' | 'custom'

  // ---------- Expand/collapse per employee ----------
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  // ---------- Build URL with date range query params ----------
  const buildUrl = (from, to) => {
    const params = new URLSearchParams();
    if (from) params.append("from_date", from);
    if (to) params.append("to_date", to);
    const qs = params.toString();
    return qs ? `${API_URL}?${qs}` : API_URL;
  };

  // ---------- Fetch list ----------
  const fetchFeedback = async (from = fromDate, to = toDate) => {
    setLoading(true);
    try {
      const res = await fetch(buildUrl(from, to), {
        method: "GET",
        headers: { ...authHeaders },
      });
      if (!res.ok) throw new Error("Failed to fetch feedback");
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.results || [];
      setFeedbackList(list);
    } catch (error) {
      console.error("Error fetching employee feedback:", error);
      NotificationManager.error("Error fetching employee feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback(defaultRange.from, defaultRange.to);
    
  }, []);

  // ---------- Date range handlers ----------
  const handleThisMonth = () => {
    const { from, to } = getThisMonthRange();
    setFromDate(from);
    setToDate(to);
    setActiveRange("current");
    fetchFeedback(from, to);
  };

  const handlePreviousMonth = () => {
    const { from, to } = getPreviousMonthRange();
    setFromDate(from);
    setToDate(to);
    setActiveRange("previous");
    fetchFeedback(from, to);
  };

  const handleFetch = () => {
    setActiveRange("custom");
    fetchFeedback(fromDate, toDate);
  };

  const handleSearch = (e) => {
    e.preventDefault();
  };

  // ---------- Derived (search on already-fetched range) ----------
  const filteredFeedback = useMemo(() => {
    if (!search) return feedbackList;
    const term = search.toLowerCase();
    return feedbackList.filter((f) => {
      const haystack = `${f.employee || ""} ${f.user || ""} ${f.content || ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [feedbackList, search]);

  // ---------- Group feedback by employee ----------
  // const groupedByEmployee = useMemo(() => {
  //   const map = {};
  //   filteredFeedback.forEach((f) => {
  //     const key = f.employee || "Unknown";
  //     if (!map[key]) {
  //       map[key] = { key, employeeName: key, feedbacks: [] };
  //     }
  //     map[key].feedbacks.push(f);
  //   });

  //   return Object.values(map)
  //     .map((group) => {
  //       const ratings = group.feedbacks.map((f) => Number(f.rating) || 0).filter((r) => r > 0);
  //       const average = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;
  //       const feedbacks = [...group.feedbacks].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  //       return { ...group, feedbacks, total: group.feedbacks.length, average };
  //     })
  //     .sort((a, b) => b.total - a.total);
  // }, [filteredFeedback]);

  // ---------- Group feedback by employee ----------
// All feedback remains visible.
// Only status === true feedback is counted in Total + Average.
const groupedByEmployee = useMemo(() => {
  const map = {};

  filteredFeedback.forEach((f) => {
    const key = f.employee || "Unknown";

    if (!map[key]) {
      map[key] = {
        key,
        employeeName: key,
        feedbacks: [],
      };
    }

    // Keep ALL feedback, both true and false
    map[key].feedbacks.push(f);
  });

  return Object.values(map)
    .map((group) => {
      // Only ACTIVE/REVIEWED feedback is used for statistics
      const activeFeedbacks = group.feedbacks.filter(
        (f) => f.status === true
      );

      const ratings = activeFeedbacks
        .map((f) => Number(f.rating) || 0)
        .filter((r) => r > 0);

      const average = ratings.length
        ? ratings.reduce((a, b) => a + b, 0) / ratings.length
        : 0;

      // Keep all feedback in UI
      const feedbacks = [...group.feedbacks].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      return {
        ...group,

        // Only status === true feedback is counted
        total: activeFeedbacks.length,
        average,

        // ALL feedback is displayed when expanded
        feedbacks,
      };
    })
    .sort((a, b) => b.total - a.total);
}, [filteredFeedback]);

  const toggleExpand = (key) => {
    setExpandedEmployee((prev) => (prev === key ? null : key));
  };

  // ---------- Modal open/close ----------
  const openEditForm = async (id) => {
    setEditingId(id);
    setShowModal(true);
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_URL}${id}/`, {
        method: "GET",
        headers: { ...authHeaders },
      });
      if (!res.ok) throw new Error("Failed to load feedback entry");
      const data = await res.json();
      setContent(data.content || "");
      setStatus(!!data.status);
    } catch (error) {
      console.error("Error loading feedback entry:", error);
      const fallback = feedbackList.find((f) => f.id === id);
      setContent(fallback?.content || "");
      setStatus(!!fallback?.status);
      NotificationManager.error("Couldn't load latest content, showing cached copy");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeForm = () => {
    setShowModal(false);
    setEditingId(null);
    setContent("");
    setStatus(false);
  };

  // ---------- Submit (update content + status) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      NotificationManager.error("Feedback content can't be empty");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}${editingId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ content, status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");

      NotificationManager.success("Feedback updated successfully");
      setFeedbackList((list) =>
        list.map((f) => (f.id === editingId ? { ...f, content, status, updated_at: new Date().toISOString() } : f))
      );
      closeForm();
    } catch (error) {
      console.error("Error saving feedback:", error);
      NotificationManager.error(error.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 pb-2 px-3">
      <style>{`
        .mef-table-wrap {
          background: #fff;
          border: 1px solid #edf0f4;
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(16,24,40,.05);
          overflow: hidden;
        }
        .mef-emp-table thead th {
          font-size: 12.5px;
          text-transform: uppercase;
          letter-spacing: .03em;
          color: #667085;
          border-bottom: 1px solid #eef1f5;
        }
        .mef-emp-row td { border-bottom: 1px solid #f2f4f7; padding-top: 12px; padding-bottom: 12px; }
        .mef-emp-row:hover { background: #f9fbff; }
        .mef-chevron { display: inline-flex; transition: transform .15s ease; color: #667085; }
        .mef-chevron.open { transform: rotate(90deg); color: #0a5fc4; }

        .mef-avatar {
          width: 38px; height: 38px; border-radius: 50%;
          background: #eef2ff; color: #4338ca;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700; flex-shrink: 0;
        }

        .mef-expand-row td { padding: 0; border-bottom: 1px solid #eef1f5; }
        .mef-expand-content { background: #f9fbfd; }
        .mef-feedback-item {
          background: #fff;
          border: 1px solid #edf0f4;
          border-radius: 10px;
        }
        .mef-feedback-item:last-child { margin-bottom: 0 !important; }

      

        .mef-stars { color: #f59e0b; display: inline-flex; gap: 2px; }
        .mef-stars svg { fill: currentColor; }
        .mef-stars svg.empty { fill: #e4e7ec; }

        .mef-btn-edit {
          border-radius: 8px;
          border: 1px solid #0a5fc4;
          color: #0a5fc4;
          font-weight: 600;
          background: #fff;
          font-size: 13px;
        }
        .mef-btn-edit:hover { background: #0a5fc4; color: #fff; }

        .mef-modal-overlay { background: rgba(16,24,40,.55); backdrop-filter: blur(2px); }
        .mef-modal { border-radius: 16px; overflow: hidden; }
        .mef-modal-header {
          background: linear-gradient(120deg, #0a5fc4 0%, #1e88e8 100%);
          color: #fff;
        }
        .mef-modal-close {
          width: 30px; height: 30px; border-radius: 50%;
          border: 1px solid rgba(255,255,255,.5);
          background: transparent; color: #fff;
          display: flex; align-items: center; justify-content: center;
        }
        .mef-modal-close:hover { background: rgba(255,255,255,.15); }

        .mef-section-label { font-size: 13.5px; font-weight: 600; color: #344054; margin-bottom: 6px; }
      `}</style>

      <div className="flex-grow-1">
        <div className="container-fluid px-2 px-md-4" style={{ paddingTop: "20px" }}>
          {/* Page Heading - same pattern as Appointments */}
          <div
            className="appointment-header row mb-4 bg-white shadow-sm border-bottom sticky-top py-3 px-md-3 px-2 rounded"
            style={{ background: "linear-gradient(135deg, #0564bf, #89CFF0" }}
          >
            <div
              className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center"
              style={{ gap: "1rem" }}
            >
              <div>
                <h3 className="text-light mb-1">Employee Feedback</h3>
                <p className="text-light mb-0">Review submissions and update entries as they're actioned</p>
              </div>

              <div className="text-md-end">
                <h5 className="text-light mb-1">
                  {groupedByEmployee.reduce((sum, g) => sum + g.total, 0)} feedback
                  {groupedByEmployee.reduce((sum, g) => sum + g.total, 0) !== 1 ? "s" : ""} found
                </h5>
                <p className="text-light mb-0">
                  {feedbackList.filter((f) => !f.status).length} InActive · {feedbackList.filter((f) => !!f.status).length} Active
                </p>
              </div>
            </div>

            <div
              className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mt-3"
              style={{ gap: "1rem" }}
            >
              <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
                <span className="text-light small">From</span>
                <input
                  type="date"
                  className="form-control date-picker-custom form-control-sm"
                  value={fromDate}
                  max={toDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setActiveRange("custom");
                  }}
                />
                <span className="text-light small">to</span>
                <input
                  type="date"
                  className="form-control date-picker-custom form-control-sm"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setActiveRange("custom");
                  }}
                />
                <button type="button" className="btn btn-outline-light btn-sm" onClick={handleFetch}>
                  Fetch
                </button>
                
              </div>

              <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
                <button
                  type="button"
                  className={`btn btn-sm ${activeRange === "previous" ? "btn-light text-primary" : "btn-outline-light"}`}
                  onClick={handlePreviousMonth}
                >
                  Previous Month
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${activeRange === "current" ? "btn-light text-primary" : "btn-outline-light"}`}
                  onClick={handleThisMonth}
                >
                  This Month
                </button>
              </div>

              <form className="d-flex" role="search" onSubmit={handleSearch}>
                <input
                  className="form-control form-control-sm"
                  type="search"
                  placeholder="Search by employee or customer"
                  aria-label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ minWidth: "250px" }}
                />
              </form>
            </div>
          </div>

          {/* Employee summary table */}
          {loading ? (
            <div className="text-center py-5 text-muted">Loading feedback...</div>
          ) : groupedByEmployee.length === 0 ? (
            <div className="text-center py-5 text-muted">No feedback found for this range.</div>
          ) : (
            <div className="table-responsive mef-table-wrap">
              <table className="table align-middle mb-0 mef-emp-table">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 36 }}></th>
                    <th>Employee Name</th>
                    <th className="text-center">Total</th>
                    <th className="text-center">Average</th>
                    <th className="text-center">Average (out of 5)</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedByEmployee.map((group) => {
                    const isOpen = expandedEmployee === group.key;
                    return (
                      <React.Fragment key={group.key}>
                        <tr className="mef-emp-row" style={{ cursor: "pointer" }} onClick={() => toggleExpand(group.key)}>
                          <td>
                            <span className={`mef-chevron ${isOpen ? "open" : ""}`}>
                              <Icon.Chevron />
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="mef-avatar">{initials(group.employeeName)}</div>
                              <span className="fw-semibold">{group.employeeName}</span>
                            </div>
                          </td>
                          <td className="text-center">{group.total}</td>
                          <td className="text-center">
                            <span className="mef-stars">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <Icon.Star key={n} className={n > Math.round(group.average) ? "empty" : ""} />
                              ))}
                            </span>
                          </td>
                          <td className="text-center fw-semibold">{group.average.toFixed(1)}/5</td>
                        </tr>

                        {isOpen && (
                          <tr className="mef-expand-row">
                            <td colSpan={5}>
                              <div className="mef-expand-content p-3">
                                {group.feedbacks.map((entry) => {
                                  // const meta = statusMeta(entry.status);
                                  return (
                                    <div key={entry.id} className="mef-feedback-item mb-2 p-3">
                                      <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: "0.75rem" }}>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          <div className="d-flex align-items-center gap-2 mb-1">
                                            <span className="fw-semibold small">{entry.user}</span>
                                            {entry.appointment && <span className="text-muted small">· Appointment #{entry.appointment}</span>}
                                            {entry.rating ? (
                                              <span className="mef-stars">
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                  <Icon.Star key={n} className={n > entry.rating ? "empty" : ""} />
                                                ))}
                                              </span>
                                            ) : null}
                                          </div>
                                          <p className="small mb-2" style={{ color: "#344054" }}>
                                            {entry.content}
                                          </p>
                                          <div className="small text-muted">
                                            Created: {formatDateTime(entry.created_at)} &nbsp;·&nbsp; Modified: {formatDateTime(entry.updated_at)}
                                          </div>
                                        </div>
                                        <div className="d-flex flex-column align-items-end" style={{ gap: "0.5rem" }}>
                                        
                                          <button
                                            className="btn btn-sm mef-btn-edit px-3 d-inline-flex align-items-center gap-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openEditForm(entry.id);
                                            }}
                                          >
                                            <Icon.Pencil /> Edit
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ---------------- Modal ---------------- */}
      {showModal && (
        <div
          className="mef-modal-overlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 2000 }}
          onClick={(e) => e.target === e.currentTarget && closeForm()}
        >
          <div className="mef-modal bg-white shadow-lg" style={{ width: "94%", maxWidth: "560px", maxHeight: "92vh", overflowY: "auto" }}>
            <form onSubmit={handleSubmit}>
              <div className="mef-modal-header d-flex align-items-center justify-content-between px-4 py-3">
                <h5 className="mb-0 fw-bold">Edit Feedback</h5>
                <button type="button" className="mef-modal-close" onClick={closeForm}>
                  <Icon.X />
                </button>
              </div>

              <div className="px-4 py-4">
                {detailLoading ? (
                  <div className="text-center text-muted py-4">Loading...</div>
                ) : (
                  <>
                    <label className="form-label mef-section-label">Content *</label>
                    <textarea
                      className="form-control"
                      rows={6}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Very professional service."
                    />

                    <label className="form-label mef-section-label mt-3">Status</label>
                    <select
                      className="form-select"
                      value={status ? "Active" : "InActive"}
                      onChange={(e) => setStatus(e.target.value === "Active")}
                    >
                      <option value="InActive">InActive</option>
                      <option value="Active">Active</option>
                    </select>
                  </>
                )}
              </div>

              <div className="d-flex justify-content-end gap-2 border-top px-4 py-3">
                <button type="button" className="btn btn-outline-secondary" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary px-4" style={{ background: "#0a5fc4", border: "none" }} disabled={saving || detailLoading}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DashboardFooter />
    </div>
  );
};

export default EmployeeFeedback;